// §4 S2 — Events tab (spec 04 §5 e10 / §2.3; the §4 head-note's third
// tab-arrival gap, closed 2026-08-08). The per-operation timeline a planner
// reads to answer "what actually happened on this order": every floor tap as
// it was recorded, in `occurred_at` order — the PHYSICAL timeline, §2.3's own
// index — with the §2.17 corrections rendered AS corrections, never as one
// more report.
//
// The load-bearing display rules, each traceable to its spec line:
//
//  * `occurred_at` renders in the FACILITY's clock (D-089 — business instants
//    are facility-local; `formatTimestamp`'s timezone arm, the
//    `facilityTodayIso` rule restated for instants), and the tab SAYS so above
//    the table rather than leaving the reader to guess whose 14:30 that is.
//  * `capture_source` is derived honestly from what the wire carries: a
//    correction payload (`correction_of_conflict_id`, §2.17 — composed
//    server-side by conflict review, never sent by a device) is an OFFICE
//    CORRECTION; otherwise a `device_id` is a floor tap and its absence is an
//    office keystroke — §3.11's "the tablet said 5980" and "someone typed
//    5980" are never the same fact, in the same words the Operations tab uses.
//  * Stale/clamped clocks are surfaced from the two wire instants with the
//    SERVER's own predicates (`eventClockStatus` mirrors §9.5), never a flag
//    the payload does not carry: >24 h skew was applied-and-flagged, a fast
//    clock was clamped for attribution, and an offline drain inside the window
//    is neither.
//  * Pagination is e10's REAL keyset cursor accumulated behind "Load more"
//    (the costs-ledger pattern) — the truncation class has bitten six times,
//    so `next_cursor` decides when the timeline ends, never row count.
//  * The e10 `event_type[]` filter has a CONTROL (the kind select): a filter
//    only the API can reach is not shipped.
//
// Fenced by `production.read` — e10's own gate — in the parent's tab strip:
// the tab is ABSENT without it, never present-and-403ing.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import {
  useDowntimeReasons,
  useFacilities,
  useScrapReasons,
  useWorkCenters,
  useWorkOrderEvents,
  type OperationEvent,
  type OperationEventType,
  type WorkOrderEventsQuery,
  type WorkOrderOperation,
} from "../hooks/production-queries";
import { eventKindKeyOf } from "../lib/event-kind";
import { eventClockStatus, formatQty, formatTimestamp, localeLabel, parseQty } from "../lib/format";

/** The eleven §5.1 kinds, in the contract's order — the filter's options. A
 * twelfth kind served by a later module still RENDERS (the row falls back to
 * its raw name rather than blanking a fact), it just is not offered here until
 * it is named. */
const EVENT_TYPES: readonly OperationEventType[] = [
  "op_started",
  "op_paused",
  "op_resumed",
  "op_completed",
  "qty_reported",
  "material_issued",
  "downtime_started",
  "downtime_ended",
  "calibration_recorded",
  "serial_lot_captured",
  "defect_handoff_flagged",
];

/** §2.17: a correction is a NEW event whose payload names the conflict it
 * compensates — the ONLY marker the wire carries, and the one a device can
 * never forge (the payload is composed server-side, floor-wire.ts). */
export function correctionConflictIdOf(ev: OperationEvent): string | undefined {
  const id = ev.payload["correction_of_conflict_id"];
  return typeof id === "string" && id.length > 0 ? id : undefined;
}

/** A payload field as the decimal-string the §5.1 wire types it — `undefined`
 * for anything else, so a malformed payload degrades to "not shown", never to
 * a fabricated 0 (the parseQty rule). */
function qtyField(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === "string" && parseQty(value) !== undefined ? value : undefined;
}

function textField(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** "+5" for a positive delta — `formatQty` owns the minus sign; a correction's
 * whole meaning is its direction, so the plus is never dropped. */
function signedQty(text: string, locale: string): string {
  const scaled = parseQty(text);
  const formatted = formatQty(text, { locale });
  return scaled !== undefined && scaled > 0n ? `+${formatted}` : formatted;
}

export function EventsTab({
  workOrderId,
  facilityId,
  operations,
}: {
  workOrderId: string;
  facilityId: string;
  operations: readonly WorkOrderOperation[];
}) {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const locale = i18n.language;

  // D-089: the facility's clock, from the same reference read every production
  // screen shares (RQ dedupes it). `undefined` until it loads or if the
  // facility is not in the caller's list — formatTimestamp then degrades to
  // the viewer's clock and the note above the table says which one is shown.
  const facilities = useFacilities();
  const facilityTimezone = facilities.data?.items.find((f) => f.id === facilityId)?.timezone;

  const workCenters = useWorkCenters(facilityId);
  const wcName = useMemo(() => {
    const map = new Map<string, string>();
    for (const wc of workCenters.data?.items ?? []) {
      map.set(wc.id, localeLabel(wc.name_i18n, locale));
    }
    return map;
  }, [workCenters.data, locale]);

  const seqByOpId = useMemo(() => {
    const map = new Map<string, number>();
    for (const op of operations) map.set(op.id, op.seq_no);
    return map;
  }, [operations]);

  // The same catalog the record form and the reports panel resolve through —
  // one vocabulary for a scrap cause, on every surface of this screen.
  const scrapReasons = useScrapReasons();
  const reasonLabel = (code: string): string => {
    const cause = (scrapReasons.data ?? []).find((c) => c.code === code);
    return cause === undefined ? code : localeLabel(cause.name_i18n, locale);
  };

  // The same rule for a §2.6 downtime reason (review finding, 2026-08-08): S10
  // and S11 render it by `name_i18n`, so a pause on this timeline must too —
  // one physical fact, one vocabulary. Falls back to the raw code while the
  // catalog loads or when it no longer names the code: a fact rendered oddly
  // beats a fact hidden (the unknown-kind rule, applied to reasons).
  const downtimeReasons = useDowntimeReasons();
  const downtimeLabel = (code: string): string => {
    const reason = (downtimeReasons.data ?? []).find((r) => r.code === code);
    return reason === undefined ? code : localeLabel(reason.name_i18n, locale);
  };

  // Kind filter (e10's `event_type[]`). A filter change RESETS the accumulated
  // pages — a cursor minted under one filter is meaningless under another.
  const [kindFilter, setKindFilter] = useState<OperationEventType | "">("");

  const [paged, setPaged] = useState<{
    key: string;
    cursor: string | undefined;
    rows: readonly OperationEvent[];
  }>({ key: "", cursor: undefined, rows: [] });
  const effective =
    paged.key === kindFilter ? paged : { key: kindFilter, cursor: undefined, rows: [] };

  const query: WorkOrderEventsQuery = {
    ...(kindFilter !== "" ? { event_type: [kindFilter] } : {}),
    ...(effective.cursor !== undefined ? { cursor: effective.cursor } : {}),
  };
  const events = useWorkOrderEvents(workOrderId, query);

  if (events.isPending && effective.rows.length === 0) {
    return <TableSkeleton rows={3} label={t("production.common.loading")} />;
  }
  if (events.error) {
    return (
      <ErrorState
        title={t("production.detail.events.error")}
        detail={events.error instanceof Error ? events.error.message : undefined}
        retryLabel={t("production.common.retry")}
        onRetry={() => void events.refetch()}
        requestId={requestIdOf(events.error)}
        requestIdLabel={t("production.common.requestId")}
      />
    );
  }

  const rows = [...effective.rows, ...(events.data?.items ?? [])];
  const nextCursor = events.data?.next_cursor ?? null;

  // The worded kind. A negative material issue is the floor RETURN riding the
  // same §5.1 row (D-081) — worded as what it physically was. An unknown kind
  // (bytes from an older/newer build) falls back to its raw name: a fact
  // rendered oddly beats a fact hidden. The lookup is the shared exhaustive
  // record (lib/event-kind.ts), so a twelfth KNOWN kind fails typecheck here
  // instead of degrading at runtime.
  const kindLabelOf = (ev: OperationEvent): string => {
    const correctionOf = correctionConflictIdOf(ev);
    if (correctionOf !== undefined) return t("production.detail.events.kind.correction");
    const issuedQty = ev.event_type === "material_issued" ? qtyField(ev.payload, "qty") : undefined;
    const isReturn = issuedQty !== undefined && (parseQty(issuedQty) ?? 0n) < 0n;
    if (isReturn) return t("production.detail.events.kind.material_returned");
    const key = eventKindKeyOf(ev.event_type);
    return key === undefined ? ev.event_type : t(key);
  };

  const sourceOf = (ev: OperationEvent): string =>
    correctionConflictIdOf(ev) !== undefined
      ? t("production.detail.events.source.correction")
      : ev.device_id !== null
        ? t("production.captureSource.floor")
        : t("production.captureSource.office");

  const columns: DataTableColumn<OperationEvent>[] = [
    {
      key: "occurred_at",
      header: t("production.detail.events.colWhen"),
      render: (ev) => {
        const clock = eventClockStatus(ev.occurred_at, ev.recorded_at);
        return (
          <>
            {formatTimestamp(ev.occurred_at, locale, facilityTimezone)}
            {clock.stale ? (
              <span className="event-clock-flag is-stale" role="note">
                {t("production.detail.events.staleFlag", { hours: clock.skewHours })}
              </span>
            ) : clock.clamped ? (
              <span className="event-clock-flag" role="note">
                {t("production.detail.events.clampedFlag")}
              </span>
            ) : null}
            {clock.stale || clock.clamped ? (
              <span className="production-hint">
                {t("production.detail.events.recordedAt", {
                  when: formatTimestamp(ev.recorded_at, locale, facilityTimezone),
                })}
              </span>
            ) : null}
          </>
        );
      },
    },
    {
      key: "seq_no",
      header: t("production.detail.ops.colSeq"),
      render: (ev) =>
        (ev.wo_operation_id === null ? undefined : seqByOpId.get(ev.wo_operation_id)) ??
        t("production.common.notAvailable"),
    },
    {
      key: "event_type",
      header: t("production.detail.events.colKind"),
      render: kindLabelOf,
    },
    {
      key: "detail",
      header: t("production.detail.events.colDetail"),
      render: (ev) => (
        <EventDetail
          ev={ev}
          locale={locale}
          correctionOf={correctionConflictIdOf(ev)}
          reasonLabel={reasonLabel}
          downtimeLabel={downtimeLabel}
        />
      ),
    },
    {
      // The bare uuid, by design — §3.11: never resolved to a name the reader's
      // role may not be able to fetch.
      key: "worker_id",
      header: term("operator"),
      render: (ev) => <code>{ev.worker_id}</code>,
    },
    {
      key: "work_center_id",
      header: term("work_center"),
      render: (ev) => wcName.get(ev.work_center_id) ?? "",
    },
    {
      key: "capture_source",
      header: t("production.detail.events.colSource"),
      render: sourceOf,
    },
  ];

  const filter = (
    <label className="filter-chip">
      <span className="filter-chip-label">{t("production.detail.events.filterKind")}</span>
      <select
        data-testid="events-kind-filter"
        value={kindFilter}
        onChange={(event) => {
          const value = event.target.value as OperationEventType | "";
          setKindFilter(value);
          setPaged({ key: value, cursor: undefined, rows: [] });
        }}
      >
        <option value="">{t("production.detail.events.filterAll")}</option>
        {EVENT_TYPES.map((kind) => (
          <option key={kind} value={kind}>
            {t(`production.detail.events.kind.${kind}`)}
          </option>
        ))}
      </select>
    </label>
  );

  if (rows.length === 0) {
    // Two different facts (the S1 rule): "no device has recorded anything on
    // this order" and "nothing of the picked kind" get different sentences,
    // and only the second keeps the filter on screen to clear.
    if (kindFilter !== "") {
      return (
        <div className="wo-panel-events">
          {filter}
          <EmptyState
            title={t("production.detail.events.emptyFilteredTitle")}
            body={t("production.detail.events.emptyFilteredBody")}
          />
        </div>
      );
    }
    return (
      <EmptyState
        title={t("production.detail.events.emptyTitle")}
        body={t("production.detail.events.emptyBody")}
      />
    );
  }

  return (
    <div className="wo-panel-events">
      {filter}
      <p className="production-hint" data-testid="events-timezone-note">
        {facilityTimezone !== undefined && facilityTimezone.length > 0
          ? t("production.detail.events.timezoneNote", { timezone: facilityTimezone })
          : t("production.detail.events.timezoneNoteFallback")}
      </p>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(ev) => ev.id}
        empty={null}
        caption={t("production.detail.tab.events")}
        rowProps={(ev) => ({
          "data-testid": `wo-event-${ev.id}`,
          "data-event-type": ev.event_type,
          "data-client-event-id": ev.client_event_id,
        })}
      />
      {nextCursor !== null ? (
        <Button
          data-testid="events-load-more"
          onClick={() => {
            setPaged({
              key: kindFilter,
              cursor: nextCursor,
              rows,
            });
          }}
        >
          {t("production.detail.events.loadMore")}
        </Button>
      ) : null}
    </div>
  );
}

/** The quantities a kind carries, worded — and NOTHING for the kinds that
 * carry none (`op_started`, `op_completed`): an empty details cell on a start
 * tap is the true statement. */
function EventDetail({
  ev,
  locale,
  correctionOf,
  reasonLabel,
  downtimeLabel,
}: {
  ev: OperationEvent;
  locale: string;
  correctionOf: string | undefined;
  reasonLabel: (code: string) => string;
  downtimeLabel: (code: string) => string;
}) {
  const { t } = useTranslation();
  const payload = ev.payload;

  if (correctionOf !== undefined) {
    // §2.17: SIGNED deltas onto the projection, referencing what they correct.
    const good = qtyField(payload, "qty_good_delta");
    const scrap = qtyField(payload, "qty_scrap_delta");
    return (
      <>
        {good !== undefined && scrap !== undefined
          ? t("production.detail.events.detail.qty", {
              good: signedQty(good, locale),
              scrap: signedQty(scrap, locale),
            })
          : null}{" "}
        <span className="production-hint">
          {t("production.detail.events.detail.correctionRef")} <code>{correctionOf}</code>
        </span>
      </>
    );
  }

  switch (ev.event_type) {
    case "qty_reported": {
      const good = qtyField(payload, "qty_good");
      const scrap = qtyField(payload, "qty_scrap");
      const reason = textField(payload, "scrap_reason_code");
      if (good === undefined || scrap === undefined) return null;
      return (
        <>
          {t("production.detail.events.detail.qty", {
            good: formatQty(good, { locale }),
            scrap: formatQty(scrap, { locale }),
          })}
          {reason !== undefined ? ` · ${reasonLabel(reason)}` : null}
        </>
      );
    }
    case "material_issued": {
      const qty = qtyField(payload, "qty");
      const uom = textField(payload, "uom") ?? "";
      if (qty === undefined) return null;
      const scaled = parseQty(qty) ?? 0n;
      return scaled < 0n
        ? t("production.detail.events.detail.returned", {
            qty: formatQty(qty.replace(/^-/u, ""), { locale }),
            uom,
          })
        : t("production.detail.events.detail.issued", { qty: formatQty(qty, { locale }), uom });
    }
    case "calibration_recorded": {
      const claimed = qtyField(payload, "machine_claimed_qty");
      const measured = qtyField(payload, "measured_qty");
      const uom = textField(payload, "uom") ?? "";
      if (claimed === undefined || measured === undefined) return null;
      return t("production.detail.events.detail.calibration", {
        claimed: formatQty(claimed, { locale }),
        measured: formatQty(measured, { locale }),
        uom,
      });
    }
    case "serial_lot_captured": {
      const direction = textField(payload, "direction");
      const identity = textField(payload, "serial_no") ?? textField(payload, "scan_code");
      const qty = qtyField(payload, "qty");
      const uom = textField(payload, "uom") ?? "";
      return (
        <>
          {direction === "consumed"
            ? t("production.detail.genealogy.consumedLabel")
            : t("production.detail.genealogy.producedLabel")}
          {identity !== undefined ? (
            <>
              {" · "}
              <code>{identity}</code>
            </>
          ) : null}
          {qty !== undefined ? ` · ${formatQty(qty, { locale })} ${uom}` : null}
        </>
      );
    }
    case "defect_handoff_flagged": {
      const qty = qtyField(payload, "qty_defective");
      const reason = textField(payload, "scrap_reason_code");
      if (qty === undefined) return null;
      return t("production.detail.events.detail.defect", {
        qty: formatQty(qty, { locale }),
        reason: reason !== undefined ? reasonLabel(reason) : "",
      });
    }
    case "op_paused":
    case "op_resumed":
    case "downtime_started": {
      // Resolved through the §2.6 catalog's `name_i18n` — the words S10's grid
      // and S11's log use for the same reason, never the raw code.
      const reason = textField(payload, "downtime_reason_code");
      return reason !== undefined
        ? t("production.detail.events.detail.downtimeReason", { reason: downtimeLabel(reason) })
        : null;
    }
    default:
      return null;
  }
}
